import { watch } from 'vue';
import PrimeVue, { defaultOptions } from 'primevue/config';
import { PRIMEUI_LICENSE, licenseExpiry } from './license';
import { BronzePreset } from './preset';
import { PRIMEVUE_Z_INDEX } from './layers';

/**
 * The cascade-layer order. It must equal the `@layer` statement at the top of
 * `src/ui/cascade.css`, which explains what each layer holds; `tests/unit/ui.install.spec.js`
 * fails if the two drift.
 */
export const CSS_LAYER_ORDER = 'bz-reset, primevue, bz-compat';

/**
 * The class PrimeVue keys its dark tokens on. Nobody sets it, on purpose, and nobody needs to.
 *
 * @why    PrimeVue 5 switches its colours by setting `color-scheme: dark` on whatever carries
 *         this class (read in the generated theme: `.bz-dark{color-scheme:dark}`). On <html>
 *         that flips the WHOLE document to the browser's dark scheme — the native selects,
 *         checkboxes and slider in Settings, the default colours of every form field, and
 *         the backdrop of the Help iframe. Since stage 3 the preset has one scheme, the dark
 *         one (`darkOnly` in preset.js), so there is nothing left for the class to switch: it
 *         stays unset. If a library overlay shows a light native scrollbar, `color-scheme:
 *         dark` goes on that container, not on the document.
 *
 *         Not `'system'`, PrimeVue's default: that would follow the TABLET's dark-mode
 *         setting, and kitchens do switch it on (it is the workaround for the white Android
 *         navigation bar in the PWA), so two tablets of the same store would render differently.
 */
export const DARK_MODE_CLASS = 'bz-dark';

/**
 * What stage 1 has to be able to see from the field, read by App.vue into `app_startup`.
 *
 * @why    `app_startup.totalMs` starts in `handleLoginSuccess`, AFTER the bundle has been
 *         parsed, the plugin installed and the app mounted — so on its own it is blind to
 *         the one cost PrimeVue adds. `uiInstallMs` is that cost, and `fcpMs` (the browser's
 *         First Contentful Paint) is the whole boot up to the first frame, bundle included.
 *         And the licence verdict is computed from the DEVICE clock, so a tablet with its
 *         date wrong paints the licence banner on that tablet only: `licenseValid` is
 *         PrimeVue's own verdict (the one that decides the banner), `licenseStatus` and
 *         `licenseDaysUntilExpiry` say why and for how long, read off the token's expiry
 *         against this device's clock (`licenseExpiry` in license.js).
 */
const report = {
    uiInstallMs: null,
    uiInstallError: null,
    licenseValid: null,
    ...licenseExpiry(PRIMEUI_LICENSE),
};

/**
 * PrimeVue's locale for one of the app's languages: the strings the adopted components read,
 * from vue-i18n, over PrimeVue's English defaults for everything nobody reads.
 *
 * The translations are taken raw with `getLocaleMessage`, never through `t()`: PrimeVue
 * fills `{0}` itself, and vue-i18n would read `{0}` as a list parameter and swallow it.
 *
 * @param {object} messages The whole vue-i18n catalogue of one locale.
 */
export function primevueLocale(messages) {
    const ours = (messages && messages.primevue) || {};
    return {
        ...defaultOptions.locale,
        ...ours,
        aria: { ...defaultOptions.locale.aria, ...(ours.aria || {}) },
    };
}

/**
 * Installs PrimeVue — plugin, licence, preset, z-index ladder and locale — and nothing
 * else: no component is registered here, and none renders in stage 1.
 *
 * @rule   A failure here is reported, never thrown. Nothing on screen depends on the library
 *         yet, so an install that breaks for any reason must leave the tablet booting the app
 *         it boots today, and say so in `app_startup.uiInstallError`.
 * @why    Judged by its result — the theme's <style> in <head> — and not by an exception,
 *         because in production an exception usually never reaches here: PrimeVue generates
 *         the theme inside a Vue watcher, and Vue logs an error in a watcher to the console
 *         and moves on (measured on `dist/`, forcing the theme engine to throw). That also
 *         means the report carries `theme-not-injected`, not the cause; the browser version
 *         that comes with every PostHog event is what to read next.
 *
 *         What the library needs from the browser, for whoever reads this later: PrimeVue's
 *         CSS lives in `@layer` (Chrome 99) and its colours in `light-dark()` (Chrome 123).
 *         The fleet, read from PostHog on 22-sep-2026: the oldest Chrome is 108, then 114,
 *         both below 123 — see docs/migracion-ui/referencia.md, "Etapa 1 — lo medido".
 *
 * @param {import('vue').App} app
 * @param {import('vue-i18n').I18n} i18n The app's instance, already installed.
 */
export function installUi(app, i18n) {
    const startedAt = performance.now();
    const localeFor = (lang) => primevueLocale(i18n.global.getLocaleMessage(lang));

    try {
        app.use(PrimeVue, {
            license: PRIMEUI_LICENSE,
            theme: {
                preset: BronzePreset,
                options: {
                    darkModeSelector: `.${DARK_MODE_CLASS}`,
                    cssLayer: { name: 'primevue', order: CSS_LAYER_ORDER },
                },
            },
            zIndex: { ...PRIMEVUE_Z_INDEX },
            locale: localeFor(i18n.global.locale.value),
        });
    } catch (error) {
        report.uiInstallError = String((error && error.message) || error);
        console.error('[ui] PrimeVue failed to install', error);
        return;
    }
    // Synchronous on purpose: installing the plugin generates the theme and injects its
    // <style> elements right here, before the first component mounts.
    report.uiInstallMs = Math.round(performance.now() - startedAt);
    try {
        // The same span as a User Timing entry, so it shows in any Chrome trace of a tablet.
        performance.measure('bz-ui-install', { start: startedAt });
    } catch (_) {
        /* older User Timing: the number above is enough */
    }
    if (!document.querySelector('style[data-primevue-style-id="global-style"]')) {
        report.uiInstallError = 'theme-not-injected';
    }

    const primevue = app.config.globalProperties.$primevue;

    // The restaurant's language arrives after login (App.vue sets the global locale); the
    // library's strings have to follow it, or a French kitchen gets "Close" on every dialog.
    watch(
        () => i18n.global.locale.value,
        (lang) => {
            primevue.config.locale = localeFor(lang);
        },
    );

    // PrimeVue's own verdict — the one that decides whether its banner appears. It resolves a
    // microtask after install, long before `app_startup` fires.
    watch(primevue.verified, (valid) => {
        report.licenseValid = valid;
    });
}

/**
 * The stage-1 fields for `app_startup`. Never throws: telemetry must not take the app down.
 */
export function uiStartupTelemetry() {
    // First Contentful Paint only measures the JavaScript boot because public/index.html paints
    // nothing on its own. A static splash there would move it before the bundle runs, and this
    // number would stop seeing the library without anything failing.
    let fcpMs = null;
    try {
        const paint = performance.getEntriesByName('first-contentful-paint')[0];
        if (paint) fcpMs = Math.round(paint.startTime);
    } catch (_) {
        /* no Paint Timing: leave it null */
    }
    return { ...report, fcpMs };
}
